import { DateTime } from "luxon";

export class Utils {
  static cpfLength = 11;
  static cnpjLength = 14;

  static OnlyNumber(field: string) {
    if ((!field) || field == '') { return '' }
    return field.replace(/[_\W]+/g, '')
  }

  static IsCGCValid(cgc: string): any {
    let _cgc = this.OnlyNumber(cgc)
    let result = this.validate(_cgc)
    return result == null
  }

  private static validate(c: any): any {
    const cpfCnpj = c.replace(/\D/g, '');
    if ([this.cpfLength, this.cnpjLength].indexOf(cpfCnpj.length) < 0) {
      return { length: true };
    }

    if (/^([0-9])\1*$/.test(cpfCnpj)) {
      return { equalDigits: true };
    }
    const cpfCnpjArr: number[] = cpfCnpj.split('').reverse().slice(2);
    cpfCnpjArr.unshift(this.buildDigit(cpfCnpjArr));
    cpfCnpjArr.unshift(this.buildDigit(cpfCnpjArr));

    if (cpfCnpj !== cpfCnpjArr.reverse().join('')) {
      return { digit: true };
    }

    return null
  }

  private static buildDigit(arr: number[]): number {
    const isCpf = arr.length < this.cpfLength;
    const digit = arr
      .map((val, idx) => val * ((!isCpf ? idx % 8 : idx) + 2))
      .reduce((total, current) => total + current) % this.cpfLength;
    if (digit < 2 && isCpf) {
      return 0;
    }
    return digit < 2 ? 0 : this.cpfLength - digit;
  }

  public static IsEmailValid(email: string) {
    return (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(email.trim())
  }

  static MoneyFormat(value: any, currencyCode: string = 'BRL', symbolDisplay: boolean = true, digits: boolean = true, numberOfDigits: number = 2) {
    if (value == null || isNaN(value)) {
      value = 0;
    }
    let numValue: number = parseFloat(value);
    if (!isFinite(numValue)) {
      numValue = 0;
    }
    if (numValue > 999999999999.99) {
      numValue = 999999999999.99;
    } else if (numValue < -999999999999.99) {
      numValue = -999999999999.99;
    }

    let result = new Intl.NumberFormat('pt-BR', {
      style: 'currency', currency:
        currencyCode, minimumFractionDigits: numberOfDigits
    }).format(numValue);

    if (digits == false) {
      result = result.substring(0, result.length - (numberOfDigits + 1))
    }
    return (symbolDisplay ? result : result.replace('R$', ''))
  }

  public static ToLuxonDate(_date: any = undefined, timestamp: boolean = false) {
    if (_date == undefined) {
      return DateTime.now().setZone('America/Sao_Paulo');
    }

    if (timestamp) {
      return DateTime.fromMillis(Number(_date), { zone: 'America/Sao_Paulo' });
    }

    if (typeof _date === 'string' && _date.endsWith('Z')) {
      _date = _date.replace('Z', '');
    }

    let date: any = DateTime.fromJSDate(_date, { zone: 'America/Sao_Paulo' });
    if (date.invalid) {
      date = DateTime.fromISO(_date, { zone: 'America/Sao_Paulo' });
      if (date.invalid) {
        date = DateTime.fromSQL(_date, { zone: 'America/Sao_Paulo' });
      }
    }

    return date;
  }

  public static FormatDateTime(date: string, format: string = 'dd/MM/yyyy HH:mm:ss') {
    let _date = this.ToLuxonDate(date);
    return _date.toFormat(format);
  }

  public static StrDateTimeToUnix(date: string) {
    const _date = this.ToLuxonDate(date);
    return _date.toMillis();
  }

  public static ListToNestedList(list: object[], keyFieldName: string, labelFieldName: string, childrenGroupFieldName: string) {
    return list.map((item: any) => {
      const itemObj: any = {};
      itemObj['key'] = item[keyFieldName];
      itemObj['label'] = item[labelFieldName];
      itemObj[childrenGroupFieldName] = list.find((children: any) => children[keyFieldName] === item);

      return itemObj;
    })
  }

  public static BuildNestedList(
    nodes: any,
    keyFieldName: string,
    labelFieldName: string,
    parentFieldName: string,
    keyFieldLabel: string = 'key',
    labelFieldLabel: string = 'label',
    childrenGroupFieldName: string = 'children'
  ): any {
    return this.BuildNestedListV2(
      nodes, keyFieldName, labelFieldName, parentFieldName,
      keyFieldLabel, labelFieldLabel, childrenGroupFieldName
    ).hierarchicalList;
  }

  public static BuildNestedListV2(
    nodes: any,
    keyFieldName: string,
    labelFieldName: string,
    parentFieldName: string,
    keyFieldLabel: string = 'key',
    labelFieldLabel: string = 'label',
    childrenGroupFieldName: string = 'children'
  ): any {

    const nodeMap = new Map<number, any>();

    nodes.forEach((node: any) => {
      let _node: any = {}
      _node[keyFieldLabel] = node[keyFieldName];
      _node[labelFieldLabel] = node[labelFieldName];
      _node[parentFieldName] = node[parentFieldName];

      nodeMap.set(node[keyFieldName], { ..._node, ...node });
    });

    // Um nó cujo pai (direto ou por herança) é ele mesmo criaria uma estrutura
    // circular em `children` ao ser linkado: percorre a cadeia de pais a partir
    // de `parentKey`, não os `children` ainda em construção, para não depender
    // da ordem de iteração de `nodes`.
    const createsCycle = (childKey: any, parentKey: any): boolean => {
      const visited = new Set<any>();
      let current = parentKey;
      while (current !== null && current !== undefined) {
        if (current === childKey) return true;
        if (visited.has(current)) return true;
        visited.add(current);
        current = nodeMap.get(current)?.[parentFieldName];
      }
      return false;
    }

    const topLevelNodes: any = [];

    nodes.forEach((node: any) => {
      if (node[parentFieldName] !== null) {
        const parent = nodeMap.get(node[parentFieldName]);
        const child = nodeMap.get(node[keyFieldName]);

        if (parent && child && !createsCycle(node[keyFieldName], node[parentFieldName])) {
          parent[childrenGroupFieldName] = parent[childrenGroupFieldName] || [];
          parent[childrenGroupFieldName].push({ ...child, data: `${parent[labelFieldLabel]} > ${child[labelFieldLabel]}` });
        }
      } else {
        topLevelNodes.push(nodeMap.get(node[keyFieldName])!);
      }
    });

    return {
      hierarchicalList: topLevelNodes,
      nodeMap: nodeMap
    };
  }

  public static UnixTimestamp(data: DateTime = DateTime.now()) {
    return data.toMillis();
  }

  public static UnixTimestampToDateTime(timestamp: number, format: string = 'dd/MM/yyyy HH:mm:ss') {
    return DateTime.fromMillis(timestamp, { zone: 'America/Sao_Paulo' }).toFormat(format);
  }

  public static NCMMasker(ncm: string) {
    const numericValue = this.OnlyNumber(ncm);
    const truncatedValue = numericValue.slice(0, 8);
    const maskedNCM = truncatedValue
      .replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3')
      .replace(/(\d{4})(\d{2})$/, '$1.$2')
      .replace(/(\d{4})$/, '$1');

    return maskedNCM;
  }

  public static FormatarCPFCNPJ(value: string): string {
    const doc = this.OnlyNumber(value);

    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (doc.length === 14) {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return value;
  }

  public static FormatarTelefone(value: string): string {
    const phone = this.OnlyNumber(value);

    if (phone.length === 11) {
      return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (phone.length === 10) {
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    return value;
  }


  public static async Digest(message: string, algo = 'SHA-256') {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);

    const hashBuffer = await crypto.subtle.digest(algo, data);

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  public static RoundNumber(value: number, decimalPlaces: number = 2) {
    if (!value || isNaN(value)) {
      value = 0
    }

    const result = value.toFixed(decimalPlaces)
    return parseFloat(result);
  }

  public static RelativeDate(days: number, from: DateTime = DateTime.now().setZone('America/Sao_Paulo')) {
    return from.plus({ days });
  }

  public static InPeriod(date: DateTime, start: DateTime = DateTime.local().startOf('month'), end: DateTime = DateTime.local().endOf('month')) {
    return date >= start && date <= end
  }

  public static GetImageData(url: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({
          data: canvas.toDataURL('image/png'),
          width: img.width,
          height: img.height
        });
      };
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  };
}
